import { fetchWithChatFeedback } from './chatFeedbackRequired';
export type MarketResearchPostHandoffProgressKey =
  | 'section_d'
  | 'section_e'
  | 'synopsis'
  | 'vendor_bids'
  | 'dd2579';

export type MarketResearchPostHandoffProgressStatus = 'completed' | 'not_completed';

export interface MarketResearchPostHandoffCompletion {
  by_email: string;
  at: string;
}

export interface MarketResearchPostHandoffProgressStage {
  key: MarketResearchPostHandoffProgressKey;
  label: string;
  status: MarketResearchPostHandoffProgressStatus;
  completed: MarketResearchPostHandoffCompletion | null;
  legacy_completion: boolean;
  /** Present only when the server discarded a malformed stored lifecycle fact. */
  lifecycle_invalid?: true;
}

export interface MarketResearchPostHandoffProgress {
  version: 3;
  /** Server-owned receipt projection; it contains no sender/recipient details. */
  handoff: {
    /** A valid receipt can remain available after a later Section C revision. */
    state: 'active' | 'historical' | 'not_handed_off' | 'date_unavailable';
    handed_off_at: string | null;
  };
  stages: MarketResearchPostHandoffProgressStage[];
  all_applicable_stages_completed: boolean;
}

const genericError = 'We could not load contracting progress. Please try again.';
const telemetryError = 'We could not record this view.';
const apiUrl = (endpoint: string): string => `${process.env.REACT_APP_API_URL || ''}${endpoint}`;
const uuidShape = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const expectedStages: readonly [MarketResearchPostHandoffProgressKey, string][] = [
  ['section_d', 'Section D Generation'],
  ['section_e', 'Section E Generation'],
  ['synopsis', 'Synopsis & MRR'],
  ['vendor_bids', 'Vendor Bids'],
  ['dd2579', 'DD/DAF 2579'],
];

// Older deployed servers retain the previous presentation label. Keep the
// transport contract compatible while always rendering the current UI name.
const legacySynopsisTransportLabel = 'Synopsis Generation';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const lifecycleKeys = new Set([
  'key', 'label', 'status', 'completed', 'legacy_completion', 'lifecycle_invalid',
]);
const requiredLifecycleKeys = [
  'key', 'label', 'status', 'completed', 'legacy_completion',
] as const;
const emailShape = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const canonicalUtcShape = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})(?:\.\d{1,6})?Z$/;

const isCanonicalUtcTimestamp = (value: string): boolean => {
  const match = canonicalUtcShape.exec(value);
  if (!match) return false;
  // Date.parse normalizes impossible calendar values (such as February 31),
  // so compare its UTC rendering with the source's whole-second value.
  const timestamp = Date.parse(`${match[1]}Z`);
  return Number.isFinite(timestamp)
    && new Date(timestamp).toISOString().slice(0, 19) === match[1];
};

const isValidCompletion = (value: unknown): value is MarketResearchPostHandoffCompletion => (
  isRecord(value)
  && Object.keys(value).length === 2
  && typeof value.by_email === 'string'
  && emailShape.test(value.by_email)
  && typeof value.at === 'string'
  && isCanonicalUtcTimestamp(value.at)
);

/**
 * This is deliberately a narrow allowlist. The terminal screen must never
 * become a transport for cursor, completion-code, artifact, or people data.
 */
const parseProjection = (value: unknown): MarketResearchPostHandoffProgress => {
  if (!isRecord(value)
    || value.version !== 3
    || !isRecord(value.handoff)
    || !['active', 'historical', 'not_handed_off', 'date_unavailable'].includes(value.handoff.state as string)
    || !(value.handoff.handed_off_at === null || (typeof value.handoff.handed_off_at === 'string'
      && isCanonicalUtcTimestamp(value.handoff.handed_off_at)))
    || !Array.isArray(value.stages)
    || typeof value.all_applicable_stages_completed !== 'boolean') {
    throw new Error(genericError);
  }
  const expected = value.stages.length === expectedStages.length - 1
    ? expectedStages.slice(0, -1)
    : value.stages.length === expectedStages.length ? expectedStages : null;
  if (!expected) throw new Error(genericError);

  const stages = value.stages.map((stage, index): MarketResearchPostHandoffProgressStage => {
    const expectedStage = expected[index];
    if (!isRecord(stage)
      || Object.keys(stage).some((key) => !lifecycleKeys.has(key))
      || !requiredLifecycleKeys.every((key) => Object.prototype.hasOwnProperty.call(stage, key))
      || stage.key !== expectedStage[0]
      || (stage.key === 'synopsis'
        ? (stage.label !== expectedStage[1] && stage.label !== legacySynopsisTransportLabel)
        : stage.label !== expectedStage[1])
      || (stage.status !== 'completed' && stage.status !== 'not_completed')
      || typeof stage.legacy_completion !== 'boolean'
      || (stage.lifecycle_invalid !== undefined && stage.lifecycle_invalid !== true)) {
      throw new Error(genericError);
    }
    const isCompleted = stage.status === 'completed';
    const isLegacy = stage.legacy_completion;
    const lifecycleInvalid = stage.lifecycle_invalid === true;
    const completion = stage.completed;
    if ((lifecycleInvalid && (isCompleted || isLegacy || completion !== null))
      || (isCompleted && isLegacy && completion !== null)
      || (isCompleted && !isLegacy && !isValidCompletion(completion))
      || (!isCompleted && (isLegacy || completion !== null))) {
      throw new Error(genericError);
    }
    return {
      key: expectedStage[0],
      label: expectedStage[1],
      status: stage.status,
      completed: isValidCompletion(completion) ? completion : null,
      legacy_completion: isLegacy,
      ...(lifecycleInvalid ? { lifecycle_invalid: true } : {}),
    };
  });
  if (value.all_applicable_stages_completed !== stages.every(
    (stage) => stage.status === 'completed',
  )) {
    throw new Error(genericError);
  }
  return {
    version: 3,
    handoff: {
      state: value.handoff.state as MarketResearchPostHandoffProgress['handoff']['state'],
      handed_off_at: value.handoff.handed_off_at as string | null,
    },
    stages,
    all_applicable_stages_completed: value.all_applicable_stages_completed,
  };
};

export async function getMarketResearchPostHandoffProgress(
  flowId: string,
): Promise<MarketResearchPostHandoffProgress> {
  let response: Response;
  try {
    response = await fetchWithChatFeedback(apiUrl(
      `/api/market-research/flows/${encodeURIComponent(flowId)}/post-handoff-progress`,
    ), { method: 'GET', credentials: 'include' });
  } catch {
    throw new Error(genericError);
  }
  if (response.status === 401) window.dispatchEvent(new Event('auth-expired'));
  if (!response.ok) throw new Error(genericError);
  return parseProjection(await response.json().catch(() => null));
}

/**
 * Best-effort terminal-view telemetry. This intentionally bypasses apiCall:
 * the narrow endpoint succeeds with an empty 204 response rather than an API
 * envelope. It accepts no browser-provided flow, role, progress, or recipient
 * data beyond the path and an idempotency-safe UUID.
 */
export async function recordMarketResearchPostHandoffProgressView(
  flowId: string,
  viewId: string,
): Promise<void> {
  if (!uuidShape.test(viewId)) throw new Error(telemetryError);
  let response: Response;
  try {
    response = await fetchWithChatFeedback(apiUrl(
      `/api/market-research/flows/${encodeURIComponent(flowId)}/post-handoff-progress/views`,
    ), {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ view_id: viewId }),
    });
  } catch {
    throw new Error(telemetryError);
  }
  if (!response.ok) throw new Error(telemetryError);
}
